import { Link } from 'react-router';
import React, { Children, type ReactNode, useState } from 'react';
import { Button, cn, Popover } from '@skupreme/shared/ui';
import { DotsThree, PencilSimple, Trash } from '@phosphor-icons/react';
import { toPlacement } from '@utility/popup-placement';
import { DeleteConfirmWithButton, DeleteModal } from './DeleteConfirm';

export function ActiveMenu({ children, activeClassName, href, ...props }) {
  const asPath = window.location.pathname;
  const child = Children.only(children);
  const childClassName = child.props.className || '';
  const to = encodeURI(href.split('?')[0]);
  let className = '';
  if (to === '/app/') {
    const childClassName = child?.props?.className || '';
    className = asPath === to ? `active ${childClassName}` : childClassName;
  } else {
    const isActive =
      to === '/app/scm' && child?.props?.level !== undefined
        ? asPath === `${to}/`
        : asPath.indexOf(to) > -1;
    className = isActive
      ? `${childClassName} ${activeClassName || 'active'}`.trim()
      : childClassName;
  }

  return (
    <Link {...props} to={href}>
      {React.cloneElement(child, {
        className: className || null,
      })}
    </Link>
  );
}

export function ActiveMenuIcon({
  hover,
  notLink,
  link: menuLink,
  activePath,
  path,
  level,
  ...props
}) {
  const asPath = window.location.pathname.split('?')[0];
  const link = menuLink.split('?')[0];

  let src = '';
  if (link === '/app') {
    src = asPath === `${link}/` ? activePath : path;
  } else if (link === '/app/scm' && level !== undefined) {
    src = asPath === `${link}/` ? activePath : path;
  } else {
    src = asPath.indexOf(link) > -1 ? activePath : path;
  }

  return <img src={src} alt="Menu" {...props} />;
}

/* The dark square that opens a row's actions; `action-edit-button` is what row hover styles reveal. */
const EDIT_BUTTON = cn(
  'action-edit-button sk:size-[26px] sk:rounded-sm sk:bg-fg sk:p-0 sk:text-surface',
  'sk:hover:bg-fg sk:hover:opacity-90',
);

type ActionListProps = {
  children: ReactNode;
  onClick?: () => void;
};

function ActionList({ children, onClick }: ActionListProps) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: closes the popover after a pick; each item handles its own keys.
    <div
      onClick={onClick}
      className="sk:-m-2 sk:flex sk:flex-col sk:[&>div]:cursor-pointer sk:[&>div]:p-2.5"
    >
      {children}
    </div>
  );
}

type ActionMenuProps = {
  children: ReactNode;
  position?: string;
  opacity?: number;
  /** Kept for callers; the kit popover has one look. */
  preventChangingBg?: boolean;
};

export function ActionMenu({ children, position, opacity }: ActionMenuProps) {
  return (
    <Popover
      aria-label="Actions"
      placement={toPlacement(position || 'top center')}
      trigger={
        <Button
          variant="ghost"
          aria-label="More actions"
          className="more-details sk:absolute sk:right-5 sk:bottom-2.5 sk:size-[30px] sk:rounded-sm sk:p-0 sk:transition-opacity sk:duration-200"
          style={{ opacity: opacity || 0 }}
        >
          <DotsThree size={20} />
        </Button>
      }
    >
      <ActionList>{children}</ActionList>
    </Popover>
  );
}

type ActionEditProps = {
  onClick?: () => void;
  bottomOffset?: boolean;
};

export function ActionEdit({ onClick, bottomOffset = false }: ActionEditProps) {
  const actionButton = (
    <>
      <Button
        onPress={() => onClick?.()}
        aria-label="Edit"
        className={cn(
          EDIT_BUTTON,
          'sk:absolute sk:top-1/2 sk:right-5 sk:-translate-y-1/2 sk:opacity-0 sk:transition-opacity sk:duration-200',
        )}
      >
        <PencilSimple size={15} />
      </Button>
      {/* @ts-expect-error - ts-migration */}
      <DeleteConfirmWithButton buttonText={<Trash color="white" size={15} />} />
    </>
  );

  if (bottomOffset) {
    return (
      <div className="sk:[&>button]:absolute sk:[&>button]:top-[calc(50%-32px)] sk:[&>button]:right-3.5">
        {actionButton}
      </div>
    );
  }

  return actionButton;
}

type ActionEditDotsProps = {
  children: ReactNode;
  position?: string;
  /** Kept for callers; the kit popover has one look. */
  preventChangingBg?: boolean;
};

export function ActionEditDots({ children, position }: ActionEditDotsProps) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={cn(
        'action-container sk:absolute sk:top-0 sk:right-0 sk:flex sk:h-full sk:w-1/5 sk:items-center sk:bg-surface sk:opacity-0 sk:shadow-card',
        open && 'open sk:opacity-100',
      )}
    >
      <Popover
        aria-label="Actions"
        isOpen={open}
        onOpenChange={setOpen}
        placement={toPlacement(position || 'top center')}
        trigger={
          <Button
            aria-label="Actions"
            className={cn(EDIT_BUTTON, 'sk:mx-auto')}
          >
            <DotsThree size={16} weight="bold" />
          </Button>
        }
      >
        <ActionList onClick={() => setOpen(false)}>{children}</ActionList>
      </Popover>
    </div>
  );
}

const ACTION_MENU_ITEM = cn(
  'sk:flex sk:items-center sk:gap-1.5 sk:rounded-option sk:text-fg',
  'sk:hover:bg-fill-quaternary sk:[&.danger]:text-danger',
);

/* One entry in a row's action menu. Callers build these inline, so most fields are optional. */
export type EditAction = {
  title: string;
  icon?: ReactNode;
  className?: string;
  disabled?: boolean;
  showConfirmation?: boolean;
  content?: ReactNode;
  confirmButtonTitle?: string;
  cancelButtonTitle?: string;
  onClick: (e?: React.MouseEvent) => void;
};

type EditActionButtonProps = {
  menus: EditAction[];
  position?: string;
  /** Kept for callers; the kit popover has one look. */
  preventChangingBg?: boolean;
};

export function EditActionButton({ menus, position }: EditActionButtonProps) {
  const [open, setOpen] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [currentAction, setCurrentAction] = useState<EditAction | null>(null);

  const actionClick = (e: React.MouseEvent, item: EditAction) => {
    if (item.showConfirmation) {
      setCurrentAction(item);
      setOpenModal(true);
    } else {
      item.onClick(e);
    }
  };

  return (
    <>
      <Popover
        aria-label="Actions"
        isOpen={open}
        onOpenChange={setOpen}
        placement={toPlacement(position || 'top center')}
        trigger={
          <Button
            aria-label="Actions"
            className="sk:size-10 sk:rounded-sm sk:border sk:border-solid sk:border-edge sk:p-0"
          >
            <DotsThree size={20} />
          </Button>
        }
      >
        <ActionList onClick={() => setOpen(false)}>
          {menus.map(item => (
            // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: legacy menu rows, matched by the row menus that share this list.
            <div
              key={item.title}
              onClick={e => actionClick(e, item)}
              className={cn(ACTION_MENU_ITEM, item.className)}
            >
              {item.icon}
              {item.title}
            </div>
          ))}
        </ActionList>
      </Popover>
      <DeleteModal
        close={() => setOpenModal(false)}
        open={openModal}
        onConfirm={currentAction?.onClick}
        cancelButtonTitle={currentAction?.cancelButtonTitle}
        confirmButtonTitle={currentAction?.confirmButtonTitle}
        content={currentAction?.content}
      />
    </>
  );
}
